import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import MovieGrid from "@/components/feature/MovieGrid";
import EmptyState from "@/components/base/EmptyState";
import { useFavorites } from "@/hooks/useFavorites";
import { usePageMeta } from "@/hooks/usePageMeta";
import { getCategoryMeta, getItemById, getRelated } from "@/utils/catalog";

export default function Detail() {
  const { id = "" } = useParams<{ id: string }>();
  const item = getItemById(id);

  const { isFavorite, toggleFavorite } = useFavorites();
  const [toast, setToast] = useState("");

  const related = useMemo(() => (item ? getRelated(item, 6) : []), [item]);

  usePageMeta(
    item ? `《${item.title}》详情 - 豆花视频` : "影片信息加载失败 - 豆花视频",
    item
      ? `《${item.title}》是一部${item.year}年${item.region}${item.categoryLabel}，类型为${item.genres.join("、")}，由${item.director}执导，${item.actors.join("、")}主演。${item.synopsis.slice(0, 60)}`
      : "抱歉，未找到该影片的信息。"
  );

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(""), 2000);
  };

  if (!item) {
    return (
      <div className="w-full px-4 md:px-8 py-20">
        <EmptyState
          icon="ri-file-warning-line"
          title="影片信息加载失败"
          description="该影片可能已下线或链接有误，返回首页浏览其他精彩内容。"
          actionLabel="返回首页"
          onAction={() => window.REACT_APP_NAVIGATE?.("/")}
        />
      </div>
    );
  }

  const categoryMeta = getCategoryMeta(item.type);
  const favorited = isFavorite(item.id);

  const handleShare = async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
      showToast("链接已复制，快分享给朋友吧");
    } catch {
      showToast("分享链接已就绪，请手动复制地址栏链接");
    }
  };

  const handleFavorite = () => {
    const added = toggleFavorite(item.id);
    showToast(added ? "已加入收藏" : "已取消收藏");
  };

  const infoRows = [
    { label: "导演", value: item.director },
    { label: "主演", value: item.actors.join("、") },
    { label: "类型", value: item.genres.join(" / ") },
    { label: "地区", value: item.region },
    { label: "年份", value: `${item.year}` },
    { label: "片长", value: item.duration },
    { label: "语言", value: item.language },
    { label: "更新", value: item.update },
  ];

  return (
    <div className="w-full">
      <section className="relative w-full">
        <div className="absolute inset-0 h-full overflow-hidden bg-foreground-950">
          {item.backdrop ? (
            <img
              src={item.backdrop}
              alt={`${item.title}剧照`}
              title={`${item.title} - 影片详情`}
              className="w-full h-full object-cover object-top"
            />
          ) : (
            <img
              src={item.poster}
              alt={`${item.title}海报`}
              title={`${item.title} - 影片详情`}
              className="w-full h-full object-cover object-top blur-sm"
            />
          )}
          <div className="absolute inset-0 hero-overlay"></div>
        </div>

        <div className="relative z-10 w-full px-4 md:px-8 pt-8 pb-10">
          <Breadcrumb
            tone="light"
            items={[
              { label: "首页", to: "/" },
              { label: categoryMeta.channel, to: `/category/${item.type}` },
              { label: item.title },
            ]}
          />

          <div className="mt-6 flex flex-col md:flex-row gap-6 md:gap-8 items-start">
            <div className="w-32 sm:w-40 md:w-52 shrink-0 rounded-lg overflow-hidden border border-background-50/20">
              <img
                src={item.poster}
                alt={`${item.title}海报`}
                title={`${item.title} - ${item.categoryLabel}`}
                className="w-full aspect-[2/3] object-cover object-top"
              />
            </div>

            <div className="flex-1 min-w-0">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary-500 text-background-50 text-xs font-medium mb-3">
                <i className="ri-price-tag-3-line"></i>
                {item.categoryLabel}
              </span>
              <h1 className="text-2xl md:text-4xl font-black text-background-50 mb-4">
                {item.title}
              </h1>

              <div className="flex flex-wrap items-center gap-3 md:gap-5 text-sm text-background-50/85 mb-5">
                <span className="flex items-center gap-1 text-accent-400 font-bold text-base">
                  <i className="ri-star-fill"></i>
                  {item.rating}
                </span>
                <span>{item.year}</span>
                <span>{item.region}</span>
                <span>{item.duration}</span>
                <span>{item.language}</span>
                <span className="flex items-center gap-1 text-primary-300">
                  <i className="ri-fire-line"></i>
                  热度 {item.popularity}
                </span>
              </div>

              <div className="flex flex-wrap gap-2 mb-6">
                {item.genres.map((g) => (
                  <span
                    key={g}
                    className="px-2.5 py-1 rounded-md bg-background-50/15 border border-background-50/25 text-background-50 text-xs"
                  >
                    {g}
                  </span>
                ))}
              </div>

              <div className="flex flex-wrap items-center gap-3">
                <button
                  onClick={handleFavorite}
                  className={`flex items-center gap-2 px-5 py-2.5 rounded-md text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                    favorited
                      ? "bg-accent-500 text-background-50 hover:bg-accent-600"
                      : "bg-primary-500 text-background-50 hover:bg-primary-600"
                  }`}
                >
                  <i className={favorited ? "ri-bookmark-fill" : "ri-bookmark-line"}></i>
                  {favorited ? "已收藏" : "收藏"}
                </button>
                <button
                  onClick={handleShare}
                  className="flex items-center gap-2 px-5 py-2.5 rounded-md bg-background-50/15 border border-background-50/40 text-background-50 text-sm font-semibold hover:bg-background-50/25 transition-colors cursor-pointer whitespace-nowrap backdrop-blur-sm"
                >
                  <i className="ri-share-line"></i>
                  分享
                </button>
                <Link
                  to={`/category/${item.type}`}
                  className="flex items-center gap-2 px-5 py-2.5 rounded-md bg-background-50/15 border border-background-50/40 text-background-50 text-sm font-semibold hover:bg-background-50/25 transition-colors cursor-pointer whitespace-nowrap backdrop-blur-sm"
                >
                  <i className="ri-arrow-left-line"></i>
                  返回分类
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="w-full px-4 md:px-8 py-10">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <div className="flex items-center gap-3 mb-4">
              <span className="w-1.5 h-6 rounded-full bg-primary-500"></span>
              <h2 className="text-lg font-bold text-foreground-950">剧情简介</h2>
            </div>
            <p className="text-sm md:text-base text-foreground-600 leading-loose">{item.synopsis}</p>
          </div>

          <div className="lg:col-span-1">
            <div className="rounded-lg border border-background-200 bg-background-100 p-6">
              <h2 className="text-base font-bold text-foreground-950 mb-4 flex items-center gap-2">
                <i className="ri-information-line text-primary-500"></i>
                影片信息
              </h2>
              <dl className="space-y-3">
                {infoRows.map((row) => (
                  <div key={row.label} className="flex gap-3 text-sm">
                    <dt className="w-14 shrink-0 text-foreground-400">{row.label}</dt>
                    <dd className="text-foreground-700 leading-relaxed">{row.value}</dd>
                  </div>
                ))}
              </dl>
              <div className="mt-5 pt-5 border-t border-background-200">
                <Link
                  to={`/category/${item.type}`}
                  className="flex items-center justify-between text-sm text-primary-600 hover:text-primary-700 transition-colors cursor-pointer"
                >
                  <span>浏览更多{categoryMeta.label}</span>
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-14">
          <div className="flex items-center gap-3 mb-6">
            <span className="w-1.5 h-6 rounded-full bg-accent-500"></span>
            <div>
              <h2 className="text-lg md:text-xl font-bold text-foreground-950">相关推荐</h2>
              <p className="mt-1 text-sm text-foreground-500">根据类型与主创为你推荐</p>
            </div>
          </div>
          {related.length > 0 ? (
            <MovieGrid items={related} columns="grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6" />
          ) : (
            <EmptyState
              icon="ri-emotion-line"
              title="暂无相关推荐"
              description="换个影片看看，也许会有更多发现。"
            />
          )}
        </div>
      </div>

      {toast && (
        <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-50 px-5 py-3 rounded-md bg-foreground-950 text-background-50 text-sm dh-fade-up">
          {toast}
        </div>
      )}
    </div>
  );
}